import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldAlert, AlertCircle, Building2, FileBadge, Map } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to={user.role === "tenant" ? "/tenant" : user.role === "contractor" ? "/contractor" : "/dashboard"} replace />;

  const handle = async (e) => {
    e?.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Connexion réussie");
      navigate(u.role === "tenant" ? "/tenant" : u.role === "contractor" ? "/contractor" : "/dashboard");
    } catch (e) {
      const msg = typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Identifiants invalides";
      setErr(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Hero / brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "linear-gradient(180deg, #023E73 0%, #012d54 100%)" }}>
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <ShieldAlert size={22} />
            </div>
            <div>
              <div className="font-display text-xl font-bold leading-tight">Warranty &amp; Risk Manager</div>
              <div className="text-xs text-white/60 uppercase tracking-wider">Cockpit Immobilier B2B</div>
            </div>
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight">Pilotez vos désordres, sinistres et garanties.</h1>
          <p className="mt-5 text-white/75 text-base max-w-md leading-relaxed">
            La plateforme unifiée pour les Property Managers, Asset Managers et équipes techniques. GPA, GBF, Décennale, Dommages-Ouvrage, Multirisque — un seul cockpit.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10" data-testid="login-features">
          <div className="flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center"><Building2 size={16} /></div>
            <div className="text-xs text-white/70 uppercase tracking-wider leading-snug">Portefeuille<br/>multi-actifs</div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center"><FileBadge size={16} /></div>
            <div className="text-xs text-white/70 uppercase tracking-wider leading-snug">Garanties<br/>&amp; sinistres</div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center"><Map size={16} /></div>
            <div className="text-xs text-white/70 uppercase tracking-wider leading-snug">Plans<br/>interactifs</div>
          </div>
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: "#023E73" }}><ShieldAlert size={18} /></div>
            <div className="font-display font-bold text-base">Warranty &amp; Risk Manager</div>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Connexion</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">Accédez à votre espace de gestion opérationnelle.</p>

          <form onSubmit={handle} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mot de passe</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-11" />
            </div>
            {err && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-md">
                <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{err}</span>
              </div>
            )}
            <Button type="submit" data-testid="login-submit" disabled={submitting} className="w-full h-11 text-white font-semibold" style={{ background: "#023E73" }}>
              {submitting ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => navigate("/forgot-password")}
              className="text-sm text-slate-500 hover:text-[#023E73] underline underline-offset-2 transition-colors"
            >
              Mot de passe oublié ?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
